/** 어드민 메뉴 노출 여부 계산 헬퍼 (순수 함수 — React 의존 없음). */
import { isSubGroup, type NavGroup, type NavItem, type NavSubGroup } from './nav-items'

/** app_settings 키 — 값은 `{ [라우트 경로]: 노출여부 }` 형태의 JSON 객체 */
export const MENU_VISIBILITY_SETTING_KEY = 'admin_menu_visibility'

/** 코드 기본값을 덮어쓰는 override 맵. 키 = NavItem.to */
export type MenuVisibilityOverrides = Record<string, boolean>

/**
 * 최종 노출 여부.
 * override가 있으면 그것, 없으면 코드 기본값(`!hidden`).
 * `locked` 항목은 override와 무관하게 항상 노출한다.
 */
export function isItemVisible(item: NavItem, overrides: MenuVisibilityOverrides): boolean {
  if (item.locked) return true
  const override = overrides[item.to]
  return typeof override === 'boolean' ? override : !item.hidden
}

/**
 * app_settings에서 읽은 값을 방어적으로 파싱한다.
 * 객체가 아니거나(배열·null 포함) 값이 boolean이 아닌 엔트리는 조용히 버린다
 * → 빈 맵 = 전부 코드 기본값.
 */
export function parseOverrides(value: unknown): MenuVisibilityOverrides {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {}
  const result: MenuVisibilityOverrides = {}
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v === 'boolean') result[key] = v
  }
  return result
}

/**
 * 항목 단위 술어로 트리를 걸러낸다.
 * 서브그룹 내부까지 적용하고, 비어버린 서브그룹·그룹은 제거한다.
 */
function pruneNavTree(groups: NavGroup[], keep: (item: NavItem) => boolean): NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items
        .map((item): NavItem | NavSubGroup | null => {
          if (!isSubGroup(item)) return keep(item) ? item : null
          const items = item.items.filter(keep)
          return items.length > 0 ? { ...item, items } : null
        })
        .filter((item): item is NavItem | NavSubGroup => item !== null),
    }))
    .filter((group) => group.items.length > 0)
}

/**
 * 사이드바 렌더용 필터 — dev 전용 항목(prod에선 제외) + 노출 여부를 함께 적용한다.
 */
export function filterNavItems(
  groups: NavGroup[],
  { isDev, overrides }: { isDev: boolean; overrides: MenuVisibilityOverrides },
): NavGroup[] {
  return pruneNavTree(groups, (item) => (isDev || !item.devOnly) && isItemVisible(item, overrides))
}

/**
 * '메뉴 관리' 화면 목록용 — dev 전용 항목만 걸러낸다.
 * 노출 여부는 적용하지 않는다 (숨긴 항목도 목록에 보여야 다시 켤 수 있다).
 */
export function manageableNavItems(groups: NavGroup[], { isDev }: { isDev: boolean }): NavGroup[] {
  return pruneNavTree(groups, (item) => isDev || !item.devOnly)
}

/** 그룹(서브그룹 포함)에 속한 모든 항목을 평탄화한다. 일괄 토글용. */
export function flattenItems(items: (NavItem | NavSubGroup)[]): NavItem[] {
  return items.flatMap((item) => (isSubGroup(item) ? item.items : [item]))
}

/**
 * 대상 항목들의 노출값을 `next`로 지정한 새 override 맵을 만든다.
 * 코드 기본값과 같아지는 항목은 맵에서 제거해 override를 최소로 유지한다
 * (= 나중에 코드 기본값이 바뀌면 그 변경을 따라간다).
 * `locked` 항목은 건드리지 않는다.
 */
export function setItemsVisibility(
  base: MenuVisibilityOverrides,
  items: NavItem[],
  next: boolean,
): MenuVisibilityOverrides {
  const result = { ...base }
  for (const item of items) {
    if (item.locked) continue
    if (next === !item.hidden) delete result[item.to]
    else result[item.to] = next
  }
  return result
}
